import React from 'react';
import { Helmet } from 'react-helmet';
import { Button, Card, Col, Collapse, Radio, Row } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { CatalogAsyncSelect } from 'components/form/selects/CatalogAsyncSelect';
import { ContentField } from 'components/form/Content/ContentField';
import { DurationField } from 'components/form/DurationField/DurationField';
import { FormActions } from 'components/Layout/FormActions/FormActions';
import { InputField } from 'components/form/base/InputField';
import { LangSelectSection } from 'components/form/LangSelectSection/LangSelectSection';
import { MainImage } from 'components/form/MainImage/MainImage';
import { Status } from 'components/form/Status/Status';
import { Places } from 'components/form/Places';
import { PriceRange } from 'components/form/PriceRange';
import { SEO } from 'components/form/SEO/SEO';
import { TextAreaField } from 'components/form/base/TextAreaField';
import {
  CatalogRegionSelect,
  TRegionType,
} from 'components/form/selects/CatalogRegionSelect';
import {
  ContentTypeEnum,
  TContentBlock,
} from 'components/form/Content/contentTypes';
import { IEventsItem, TPlace } from 'store/slices/events/interfaces';
import { useDeepEffect } from 'utils/useDeepEffect';
import { isEmpty, isString } from 'utils/helpers';
import { TImage } from 'types/image';
import { TSEO } from 'types/seo';
import { PREVIEW_ASPECT_RATIO } from 'constants/image';
import { LangEnumKeys } from 'constants/lang';
import { Statuses } from 'constants/status';

import { mapValues, mapValuesToDraft } from '../formUtils';
import { validationSchema } from '../formValidation';
import styles from './EventsForm.module.less';

export interface IFormValues extends TSEO {
  ageRestriction: string;
  category: number | { value: number; label: string };
  content: TContentBlock[];
  duration: string;
  externalUrl: string;
  gallery: TImage[];
  heroImage: TImage;
  image: TImage;
  intro?: string;
  lang: LangEnumKeys | { value: LangEnumKeys; label: string };
  name: string;
  region: TRegionType;
  shortDescription: string;
  sortPriority: number | null;
  sortPriorityMainPage: number | null;
  status: Statuses;
  places: TPlace[];
  price: {
    price: number;
    maxPrice: number;
    freePrice: boolean;
  };
  tags: number[] | { value: number; label: string }[];
  ogImage: any;
}

type TProps = {
  initialValues: IFormValues;
  onFinish: (values: IEventsItem) => void;
  onSaveDraft?: (values: Omit<IEventsItem, 'status'>) => void;
  cancelButtonHandler: () => void;
  isDraft: boolean;
  isLoading: boolean;
};

export const ageRestriction = ['0+', '6+', '12+', '16+', '18+'];

export const EventsForm: React.FC<TProps> = ({
  initialValues,
  onFinish,
  onSaveDraft,
  cancelButtonHandler,
  isDraft,
  isLoading,
}) => {
  const [activeCollpaseTabs, setActiveCollpaseTabs] = React.useState([
    'content',
    'gallery',
    'ageRestriction',
    'places',
    'price',
    'externalUrl',
    'duration',
    'seo',
    'status',
  ]);
  const resolver = yupResolver(validationSchema);

  const methods = useForm({
    defaultValues: initialValues,
    resolver,
  });

  const handleSaveAsDraft = () => {
    const formValues = mapValuesToDraft(methods.getValues());
    onSaveDraft(formValues);
  };

  const submit = values => {
    const preparedValues = mapValues(values) as unknown as IEventsItem;

    preparedValues.ogTitle = values.ogTitle;
    preparedValues.ogDescription = values.ogDescription;
    preparedValues.ogType = values.ogType;
    preparedValues.ogUrl = values.ogUrl;
    preparedValues.ogSiteName = values.ogSiteName;

    onFinish(preparedValues);
  };

  useDeepEffect(() => {
    methods.reset(initialValues);
  }, [initialValues]);

  useDeepEffect(() => {
    if (!isEmpty(methods.formState.errors)) {
      setActiveCollpaseTabs([
        ...Array.from(
          new Set([
            ...activeCollpaseTabs,
            ...Object.keys(methods.formState.errors),
          ])
        ),
      ]);
    }
  }, [methods.formState.errors]);

  const lang = methods.watch('lang');

  useDeepEffect(() => {
    if (methods.formState.isDirty) {
      methods.reset({ ...methods.getValues(), category: null, tags: [] });
    }
  }, [lang]);

  const langValue = isString(lang) ? lang : lang.value;

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(submit)}
        className='ant-form ant-form-vertical indent-top'
      >
        <Helmet>
          <title>{methods.watch('metaTitle') || 'Default Title'}</title>
          <meta
            name='description'
            content={methods.watch('metaDescription') || 'Default description'}
          />
          <meta name='keywords' content={methods.watch('metaKeywords')} />
          <meta property='og:title' content={methods.watch('ogTitle')} />
          <meta
            property='og:description'
            content={methods.watch('ogDescription') || 'Default OG Description'}
          />
          <meta property='og:image' content={methods.watch('ogImage') || ''} />
          <meta property='og:type' content={methods.watch('ogType') || ''} />
          <meta property='og:url' content={methods.watch('ogUrl') || ''} />
          <meta
            property='og:ogSiteName'
            content={methods.watch('ogSiteName') || ''}
          />
        </Helmet>

        <LangSelectSection />
        <Card title='Общая информация' bordered={false}>
          <Row gutter={[16, 0]}>
            <Col flex={'0 0 100px'}>
              <MainImage
                name='image'
                label='Превью'
                required
                aspectRatio={PREVIEW_ASPECT_RATIO}
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <InputField
                name='name'
                label='Название события'
                required
                placeholder='Введите название'
                showCount
                maxLength={255}
              />
            </Col>
          </Row>
          <Row gutter={[24, 0]}>
            <Col xs={{ span: 24 }} md={{ span: 8 }}>
              <CatalogAsyncSelect
                name='category'
                label='Категория'
                placeholder='Выберите категорию'
                required
                requestParams={{
                  entityType: 'events',
                  fieldType: 'categories',
                  lang: langValue,
                }}
              />
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 16 }}>
              <CatalogAsyncSelect
                name='tags'
                label='Tеги'
                placeholder='Выберите теги'
                requestParams={{
                  entityType: 'events',
                  fieldType: 'tags',
                  lang: langValue,
                }}
                mode='multiple'
              />
            </Col>
          </Row>
          <Col xs={{ span: 24 }} md={{ span: 10 }}>
            <CatalogRegionSelect
              name='region'
              label='Регион'
              placeholder='Выберите регион'
              required
            />
          </Col>
          <Row>
            <Col span={24}>
              <InputField
                name='shortDescription'
                label='Краткое описание'
                required
                placeholder='Описание'
                showCount
                maxLength={300}
              />
            </Col>
          </Row>
        </Card>
        <Collapse
          bordered={false}
          expandIconPosition='end'
          onChange={(value: string[]) => setActiveCollpaseTabs(value)}
          activeKey={activeCollpaseTabs}
        >
          <Collapse.Panel header='Описание события' key='content'>
            <Row>
              <Col span={24}>
                <TextAreaField
                  label='Введение'
                  name='intro'
                  placeholder='Текст'
                  showCount
                  maxLength={700}
                  rows={10}
                />
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <ContentField
                  enabledTypes={[
                    ContentTypeEnum.image,
                    ContentTypeEnum.collage,
                    ContentTypeEnum.verticalCollage,
                    ContentTypeEnum.gallery,
                    ContentTypeEnum.video,
                    ContentTypeEnum.widget,
                    ContentTypeEnum.quote,
                    ContentTypeEnum.separator,
                    ContentTypeEnum.htmlCode,
                    ContentTypeEnum.reference,
                  ]}
                  label='Описание'
                  name='content'
                  placeholder='Начните печатать текст'
                  required
                  withWidgetsDescription
                  withPreparingForModeration
                />
              </Col>
            </Row>
          </Collapse.Panel>
          <Collapse.Panel header='Возрастное ограничение' key='ageRestriction'>
            <Controller
              name='ageRestriction'
              render={({ field }) => (
                <Radio.Group {...field}>
                  {ageRestriction.map(item => (
                    <Radio.Button
                      key={item}
                      className={styles.ageRestrictionRadioButton}
                      value={item}
                    >
                      {item}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              )}
            />
          </Collapse.Panel>
          <Collapse.Panel header='Место и время проведения' key='places'>
            <Places name='places' />
          </Collapse.Panel>
          <Collapse.Panel header='Стоимость' key='price'>
            <PriceRange name='price' />
          </Collapse.Panel>
          <Collapse.Panel header='Продажа на внешнем сайте' key='externalUrl'>
            <InputField
              name='externalUrl'
              label='Ссылка на продажу на стороннем сайте'
              placeholder='Ссылка на продажу на стороннем сайте'
              addonBefore={<GlobalOutlined />}
              formItemStyle={{ marginBottom: 0 }}
            />
          </Collapse.Panel>
          <Collapse.Panel header='Продолжительность события' key='duration'>
            <Col xs={{ span: 24 }} sm={{ span: 8 }}>
              <DurationField
                name='duration'
                label='Продолжительность события'
                suffixIcon={null}
                minuteStep={5}
              />
            </Col>
          </Collapse.Panel>
          <Collapse.Panel header='SEO' key='seo'>
            <SEO />
          </Collapse.Panel>
          <Collapse.Panel header='Публикация' key='status'>
            <Status status={initialValues.status} isStatusesLimited />
          </Collapse.Panel>
        </Collapse>
        <FormActions>
          <Button onClick={cancelButtonHandler} disabled={isLoading}>
            Отмена
          </Button>
          {isDraft && (
            <Button onClick={handleSaveAsDraft} loading={isLoading}>
              Сохранить как черновик
            </Button>
          )}
          <Button type='primary' htmlType='submit' loading={isLoading}>
            Сохранить
          </Button>
        </FormActions>
      </form>
    </FormProvider>
  );
};
